import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  ClipboardCheck,
  AlertTriangle,
  ListTodo,
  Building2,
  Users,
  Settings,
  Bell,
  User,
  ChevronLeft,
  FileWarning,
  LibraryBig,
  FileText,
  BarChart3,
  Calendar,
  Grid3X3,
  TrendingUp,
  Wrench,
  Sparkles,
  Activity,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";

const navGroups = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: null },
      { to: "/compliance-dashboard", label: "Compliance", icon: BarChart3, roles: null },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/frameworks", label: "Frameworks", icon: LibraryBig, roles: null },
      { to: "/controls", label: "Controls", icon: ShieldCheck, roles: null },
      { to: "/assessments", label: "Self-Assessments", icon: FileText, roles: null },
      { to: "/evidence", label: "Evidence", icon: FileText, roles: null },
    ],
  },
  {
    label: "Audit & Risk",
    items: [
      { to: "/audits", label: "Audits", icon: ClipboardCheck, roles: null },
      { to: "/audit-calendar", label: "Audit Calendar", icon: Calendar, roles: null },
      { to: "/risks", label: "Risk Register", icon: AlertTriangle, roles: null },
      { to: "/risk-matrix", label: "Heat Map", icon: Grid3X3, roles: null },
      { to: "/risk-trends", label: "Risk Trends", icon: TrendingUp, roles: null },
      { to: "/findings", label: "Findings", icon: FileWarning, roles: null },
      { to: "/remediations", label: "Remediations", icon: Wrench, roles: null },
      { to: "/tasks", label: "My Tasks", icon: ListTodo, roles: null },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/advisor", label: "AI Advisor", icon: Sparkles, roles: null },
      { to: "/reports", label: "Reports", icon: FileText, roles: null },
      { to: "/activity-log", label: "Activity Log", icon: Activity, roles: null },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/organizations", label: "Organizations", icon: Building2, roles: ["admin"] },
      { to: "/users", label: "Users", icon: Users, roles: ["admin", "compliance_officer"] },
      { to: "/notifications", label: "Notifications", icon: Bell, roles: null },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/profile", label: "Profile", icon: User, roles: null },
      { to: "/settings", label: "Settings", icon: Settings, roles: null },
    ],
  },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const location = useLocation();

  const canSee = (item) => !item.roles || item.roles.includes(user?.role);

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "hidden lg:flex flex-col shrink-0 h-screen sticky top-0 border-r border-border bg-card transition-[width] duration-200",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Brand */}
      <div className="h-16 flex items-center gap-2 px-4 border-b border-border">
        <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold text-foreground">CCAMS</span>
            <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              Compliance Suite
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
        {navGroups.map((group) => {
          const visible = group.items.filter(canSee);
          if (visible.length === 0) return null;
          return (
            <div key={group.label}>
              {!collapsed && (
                <div className="px-3 mb-2 label-eyebrow">{group.label}</div>
              )}
              <ul className="space-y-0.5">
                {visible.map((item) => {
                  const Icon = item.icon;
                  const active =
                    location.pathname === item.to ||
                    (item.to !== "/dashboard" && location.pathname.startsWith(item.to));
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={cn(
                          "group flex items-center gap-3 rounded-md text-sm font-medium transition-colors relative",
                          collapsed ? "justify-center h-10 w-12 mx-auto" : "h-10 px-3",
                          active
                            ? "bg-primary-subtle text-primary dark:bg-primary/15"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        )}
                        title={collapsed ? item.label : undefined}
                      >
                        {active && (
                          <span className="absolute left-0 top-1 bottom-1 w-1 rounded-r bg-primary" />
                        )}
                        <Icon className="w-5 h-5 shrink-0" />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <button
        data-testid="sidebar-collapse-toggle"
        onClick={onToggle}
        className="mx-2 mb-3 h-10 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center justify-center gap-2 text-xs font-medium"
      >
        <ChevronLeft className={cn("w-4 h-4 transition-transform", collapsed && "rotate-180")} />
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}
